import * as Yup from 'yup';

import { TOTAL_NET_WORTH_OPTION_LIST, LIQUID_NET_WORTH_OPTION_LIST } from './optionList.constants';

const validationMessages = {
  employmentStatusRequired: 'Employment Status is required',
  employmentStatusInvalid: 'Employment Status is invalid',
  employerNameRequired: 'Employer Name is required',
  employerNameInvalid: 'Employer Name is invalid',
  yearsEmployedRequired: 'Years Employed is required',
  yearsEmployedInvalid: 'Years Employed is invalid',

  addressLineOneRequired: 'Address line 1 is required',
  addressLineOneInvalid: 'Adress Line 1 can have a maximum length of 30 characters',
  addressLineTwoInvalid: 'Adress Line 2 can have a maximum length of 30 characters',
  cityRequired: 'City is required',
  cityInvalid: 'City can have a maximum length of 30 characters',
  stateRequired: 'State is required',
  stateInvalid: 'State is invalid',
  postalCodeRequired: 'Postal Code is required',
  postalCodeInvalid: 'Postal Code value should be between 0 and 100000',
  countryRequired: 'Country is required',
  countryInvalid: 'Country is invalid',

  annualIncomeRequired: 'Annual Income is required',
  annualIncomeInvalid: 'Annual Income is invalid',
  totalNetWorthRequired: 'Total Net Worth is required',
  totalNetWorthInvalid: 'Total Net Worth is invalid',
  liquidNetWorthRequired: 'Liquid Net Worth is required',
  liquidNetWorthInvalid: 'Liquid Net Worth is invalid',
  jobTitleRequired: 'Job Title is required',
  jobTitleInvalid: 'Job Title length should not be more than 50 characters',
  taxBracketRequired: 'Tax Bracket is required',
  taxBracketInvalid: 'Tax Bracket is invalid',
  fieldShouldContainOnlyLetters: 'Field should contain only letters',
};

export const upsertAccountHolderFinancialInformationAssetsValidation = Yup.object().shape({
  annualIncome: Yup.string()
    .required(validationMessages.annualIncomeRequired)
    .notOneOf(['-1'], validationMessages.annualIncomeInvalid),
  totalNetWorth: Yup.string()
    .required(validationMessages.totalNetWorthRequired)
    .notOneOf(['-1'], validationMessages.totalNetWorthInvalid)
    .test(
      'compare',
      'Total Net Worth can not be smaller than Liquid Net Worth.',
      function (totalNetWorth: string | any, schema: string | any) {
        if (schema !== undefined && schema !== null) {
          return (
            Object.values(LIQUID_NET_WORTH_OPTION_LIST).findIndex(
              item => item.value === schema?.parent?.liquidNetWorth,
            ) <= Object.values(TOTAL_NET_WORTH_OPTION_LIST).findIndex(item => item.value === totalNetWorth)
          );
        } else return false;
      },
    ),
  liquidNetWorth: Yup.string()
    .required(validationMessages.liquidNetWorthRequired)
    .notOneOf(['-1'], validationMessages.liquidNetWorthInvalid)
    .test(
      'compare',
      'Liquid Net Worth can not be greater than Total Net Worth.',
      function (liquidNetWorth: string | any, schema: string | any) {
        if (schema !== undefined && schema !== null) {
          return (
            Object.values(TOTAL_NET_WORTH_OPTION_LIST).findIndex(
              item => item.value === schema?.parent?.totalNetWorth,
            ) >= Object.values(LIQUID_NET_WORTH_OPTION_LIST).findIndex(item => item.value === liquidNetWorth)
          );
        } else return false;
      },
    ),
  taxBracket: Yup.string()
    .required(validationMessages.taxBracketRequired)
    .notOneOf(['-1'], validationMessages.taxBracketInvalid),
});

export const upsertAccountHolderFinancialInformationEmploymentValidation = Yup.object().shape({
  employmentStatus: Yup.string()
    .required(validationMessages.employmentStatusRequired)
    .notOneOf(['-1'], validationMessages.employmentStatusInvalid),
  employerName: Yup.string()
    .when('employmentStatus', {
      is: (employmentStatus: string) => employmentStatus === 'employed',
      then: Yup.string()
        .max(50, validationMessages.employerNameInvalid)

        .required(validationMessages.employerNameRequired),
    })
    .trim()
    .max(50, validationMessages.employerNameInvalid),
  jobTitle: Yup.string()
    .when('employmentStatus', {
      is: (employmentStatus: string) => employmentStatus === 'employed',
      then: Yup.string().required(validationMessages.jobTitleRequired).max(50, validationMessages.jobTitleInvalid),
    })
    .trim()
    .max(100, validationMessages.jobTitleInvalid),
  yearsEmployed: Yup.string()
    .when('employmentStatus', {
      is: (employmentStatus: string) => employmentStatus === 'employed',
      then: Yup.string()
        .required(validationMessages.yearsEmployedRequired)
        .notOneOf(['-1'], validationMessages.yearsEmployedInvalid),
    })
    .notOneOf(['-1'], validationMessages.yearsEmployedInvalid),
  address1: Yup.string()
    .trim()
    .max(30, validationMessages.addressLineOneInvalid)
    .matches(/^[^#]*$/, 'Address Line 1 cannot contain "#" character')
    .when('employmentStatus', {
      is: (employmentStatus: string) => employmentStatus === 'employed',
      then: Yup.string()
        .required(validationMessages.addressLineOneRequired)
        .max(30, validationMessages.addressLineOneInvalid),
    }),
  address2: Yup.string()
    .trim()
    .max(30, validationMessages.addressLineTwoInvalid)
    .matches(/^[^#]*$/, 'Address Line 1 cannot contain "#" character'),
  country: Yup.string().when('employmentStatus', {
    is: (employmentStatus: string) => employmentStatus === 'employed',
    then: Yup.string().required(validationMessages.countryRequired).notOneOf(['-1'], validationMessages.countryInvalid),
  }),
  state: Yup.string().when('employmentStatus', {
    is: (employmentStatus: string) => employmentStatus === 'employed',
    then: Yup.string().when('country', {
      is: (country: string) => country === 'USA',
      then: Yup.string().required(validationMessages.stateRequired),
    }),
  }),
  city: Yup.string()
    .trim()
    .when('employmentStatus', {
      is: (employmentStatus: string) => employmentStatus === 'employed',
      then: Yup.string().when('country', {
        is: (country: string) => country === 'USA',
        then: Yup.string().required(validationMessages.cityRequired).max(30, validationMessages.cityInvalid),
      }),
    }),
  postalCode: Yup.string().when('employmentStatus', {
    is: (employmentStatus: string) => employmentStatus === 'employed',
    then: Yup.string()
      .required(validationMessages.postalCodeRequired)
      .max(6)
      .test('Digits only', 'Postal Code should have digits only', (value: any) => /^\d+$/.test(value))
      .test(
        'Range size',
        'Postal Code value should be between 0 and 100000',
        (value: any) => 0 < Number(value) && Number(value) < 100000,
      ),
  }),
});
